/**
 * H-131 create-entry funnel — consume the storefront's multi-entry params
 * (docs/handoffs/H-2026-09-12-131-material-card.md §4):
 *
 *   ?source={images.id}  — a material card's design button. Resolve the row via
 *                          the anon images detail endpoint, slot it as the
 *                          source (typed library ref, ADR-005 #10), and the
 *                          studio's IMAGE_SELECT listener skips the hero act
 *                          straight into the gallery reveal (startGallery).
 *   ?entry=sources       — the hero / mobile-fab "use your own image" entry.
 *                          Land on the hero act with the four-pipe source
 *                          picker already open (upload pipe first).
 *
 * H-167 PDP deep link (docs/handoffs/H-2026-10-15-167-artwork-source-deeplink.md):
 *   ?source=<artwork url> — the PDP "MAKE IT MINE" CTA carries the product's
 *                          custom.artwork_source metafield. The URL is already
 *                          whitelist-validated by parseCreateEntryParams
 *                          (cdn.mamamiya.store / cdn.shopify.com only). Slot it
 *                          as an untyped external source (the typed asset ref is
 *                          materialized lazily at generate time, same pattern as
 *                          ?injectImage), drive the gallery via IMAGE_SELECT,
 *                          and fire the bundle/vision read so the source's
 *                          style tags render without any user action.
 *                          A present-but-invalid source param is refused with a
 *                          toast (never silently dropped).
 *
 * Runs AFTER initRouter(): the studio shell and its IMAGE_SELECT listener
 * only exist once the create mode has activated.
 */

import { CONFIG } from '../../core/config.js'
import { store } from '../../core/store.js'
import { eventBus, EVENTS } from '../../core/event-bus.js'
import { apiRequest } from '../../api/index.js'
import { showToast } from '../../utils/toast.js'
import { useImageAsSource } from '../create-ui/image-roles.js'
import { loadSourceCards } from '../create/dialog/image-slots.js'
import { openEntrySources } from './index.js'

export async function applyCreateEntry(entry) {
  if (entry?.sourceUrl) {
    store.setState('sourceImage', { url: entry.sourceUrl, source: 'deeplink' })
    // The studio's IMAGE_SELECT listener skips the hero act straight into the
    // gallery reveal. noVision: the cards read below owns the vision pass.
    eventBus.emit(EVENTS.IMAGE_SELECT, {
      url: entry.sourceUrl,
      id: null,
      source: 'source-slot',
      noVision: true,
    })
    // Image-is-the-prompt: read the source's cards (bundle-first, vision
    // fallback) so its style tags render unprompted. Non-blocking by design.
    loadSourceCards(entry.sourceUrl, null)
    return
  }
  if (entry?.sourceRejected) {
    showToast('This artwork link is not supported — start from any image instead', 'error')
    return
  }
  if (entry?.source) {
    const res = await apiRequest(
      `${CONFIG.FRONTEND_API}/api/images/${encodeURIComponent(entry.source)}`
    )
    const img = res?.data
    const url = img?.url || img?.url_thumb
    if (!url) throw new Error(`source image ${entry.source} unavailable`)
    // silent: the gallery reveal is the feedback — no "Set as source" toast.
    useImageAsSource({ url, id: img.id ?? entry.source, title: img.title || undefined }, 'library', {
      silent: true,
    })
    return
  }
  if (entry?.entry === 'sources') openEntrySources()
}
